import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getFeatureInfo } from '../../../common/getFeatureInfo';
import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';
import { Playlist } from '../playlist/Playlist';

import type { FlagsContainer } from './AudioContainer';
import { AudioFeedbackFooter } from './AudioFeedbackFooter';
import { AudioHeaderPanel } from './AudioHeaderPanel';
import { AudioPlayerError } from './AudioPlayerError';
import { AudioTimeIndicator } from './AudioTimeIndicator';
import { MiniplayerControls } from './controls/MiniplayerControls';
import { OverflowMenu } from './menu/OverflowMenu';
import { PanelWrapper } from './menu/PanelWrapper';

const i18n = defineMessages({
	closeButtonLabel: {
		id: 'audio.audio-player.close-player-button',
		defaultMessage: 'Close audio player',
		description: 'The label used for the X icon to close the audio player',
	},
});

const styles = cssMap({
	expandedAudioPlayerContainer: {
		width: '380px',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	audioPlayerMinHeight: {
		minHeight: '470px',
	},
	audioPlayerWithPendingContent: {
		height: '532px',
	},
	buttonsContainer: {
		paddingInline: token('space.050'),
	},
	controlsContainer: {
		height: '404px',
	},
});

const backgroundStyle = {
	background: `linear-gradient(180deg, ${token('elevation.surface')} 50%, ${token('elevation.surface.sunken')} 100%)`,
};

type ExpandedAudioPlayerProps = {
	audioFeatures: AudioFeature[];
	flags: FlagsContainer;
};

export const ExpandedAudioPlayer = ({
	audioFeatures,
	flags,
}: ExpandedAudioPlayerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { activeFeature, cloudId, activeMenu, closeAudioPlayer, pendingContent, setActiveMenu } =
		useAudioPlayerStore();
	const { state: streamingState } = useAudioPlayerStreamingStore();

	const activeFeatureInfo = getFeatureInfo(activeFeature, audioFeatures);

	const hasOverflowMenu =
		activeFeatureInfo?.menuConfig?.items && activeFeatureInfo?.menuConfig?.items.length > 0;

	const isErrorState = streamingState.state === AudioStreamingState.ERROR;
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;

	if (activeMenu && activeMenu.renderPanel) {
		return (
			<Box xcss={styles.expandedAudioPlayerContainer} style={backgroundStyle}>
				<PanelWrapper
					header={activeMenu.renderPanel.header}
					onBack={() => setActiveMenu(undefined)}
				>
					{activeMenu.renderPanel?.body()}
				</PanelWrapper>
			</Box>
		);
	}

	return (
		<Box
			xcss={cx(
				styles.expandedAudioPlayerContainer,
				styles.audioPlayerMinHeight,
				pendingContent && !isUninitialized && styles.audioPlayerWithPendingContent,
			)}
			style={backgroundStyle}
		>
			<Stack spread="space-between" xcss={styles.controlsContainer}>
				<Stack space="space.250" alignInline="center">
					<Inline
						alignInline="end"
						alignBlock="center"
						xcss={styles.buttonsContainer}
						grow="fill"
						spread={hasOverflowMenu ? 'space-between' : undefined}
					>
						{hasOverflowMenu && <OverflowMenu items={activeFeatureInfo?.menuConfig!.items} />}
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={CrossIcon}
							label={formatMessage(i18n.closeButtonLabel)}
							onClick={closeAudioPlayer}
						/>
					</Inline>
					<AudioHeaderPanel featureList={audioFeatures} cloudId={cloudId} flags={flags} />
					{isErrorState ? (
						<Flex justifyContent="center">
							<AudioPlayerError error={streamingState.error} />
						</Flex>
					) : (
						<>
							<MiniplayerControls />
							{!isUninitialized && !pendingContent && <AudioTimeIndicator />}
						</>
					)}
				</Stack>
				<Box>
					<AudioFeedbackFooter />
				</Box>
			</Stack>
			<Playlist cloudId={cloudId} flags={flags} />
		</Box>
	);
};
